import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Segmented, Input } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import Avatar from './Avatar';
import conversationService from '../../services/conversationService';
import { T } from './utils';

/**
 * Ouvrir une conversation : directe (une personne) ou groupe (titre + plusieurs).
 * En mode « ajouter des membres », seul le choix de personnes est proposé.
 */
export default function NouvelleConversationModal({ ouverte, surFermer, surCreer, modeAjout = false, exclure = [] }) {
  const { t } = useTranslation('conversations');
  const [type, setType] = useState('DIRECT');
  const [titre, setTitre] = useState('');
  const [filtre, setFiltre] = useState('');
  const [contacts, setContacts] = useState([]);
  const [choisis, setChoisis] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [tente, setTente] = useState(false);

  useEffect(() => {
    if (!ouverte) return;
    setType(modeAjout ? 'GROUPE' : 'DIRECT'); setTitre(''); setFiltre(''); setChoisis([]); setTente(false);
    setChargement(true);
    conversationService.contacts().then(setContacts).catch(() => setContacts([])).finally(() => setChargement(false));
  }, [ouverte, modeAjout]);

  const visibles = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return contacts
      .filter((c) => !exclure.includes(c.utilisateurTrackingId))
      .filter((c) => !f || c.nomComplet.toLowerCase().includes(f) || (c.organisme || '').toLowerCase().includes(f));
  }, [contacts, filtre, exclure]);

  const basculer = (id) => {
    if (type === 'DIRECT' && !modeAjout) { setChoisis([id]); return; }
    setChoisis((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  /* Ce qui manque, dit en clair. « Ouvrir » était grisé tant qu'un groupe n'avait pas
     de titre : on cliquait, rien ne se passait, et rien ne disait pourquoi. */
  const manqueTitre = type === 'GROUPE' && !modeAjout && titre.trim().length === 0;
  const manquePersonne = choisis.length === 0;
  const valide = !manqueTitre && !manquePersonne;

  // Passer de Directe à Groupe garde la personne déjà choisie ; l'inverse n'en garde qu'une.
  const changerType = (v) => {
    setType(v);
    if (v === 'DIRECT') setChoisis((prev) => prev.slice(0, 1));
  };

  const valider = async () => {
    setTente(true);
    if (!valide) return;
    setEnvoi(true);
    try {
      if (modeAjout) await surCreer({ membres: choisis });
      else await surCreer({ type, titre: type === 'GROUPE' ? titre.trim() : null, membres: choisis });
      surFermer();
    } finally { setEnvoi(false); }
  };

  return (
    <Modal
      open={ouverte} onCancel={surFermer} onOk={valider} okButtonProps={{ loading: envoi, style: { background: T.vert } }}
      okText={modeAjout ? t('modale.ajouter') : t('modale.ouvrir')} cancelText={t('modale.annuler')} width={480} destroyOnClose
      title={modeAjout ? t('modale.ajouterMembres') : t('modale.nouvelle')}
    >
      {!modeAjout && (
        <Segmented block value={type} onChange={changerType} style={{ marginBottom: 12 }}
          options={[{ value: 'DIRECT', label: t('modale.directe') }, { value: 'GROUPE', label: t('modale.groupe') }]} />
      )}
      {type === 'GROUPE' && !modeAjout && (
        <div style={{ marginBottom: 12 }}>
          <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder={t('modale.titreGroupe')} maxLength={160}
            status={tente && manqueTitre ? 'error' : undefined} autoFocus />
          {tente && manqueTitre && <div style={{ color: T.rouge, fontSize: T.taille.mini, marginTop: 4 }}>{t('modale.titreManquant')}</div>}
        </div>
      )}
      <Input.Search value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder={t('modale.rechercherPersonne')} allowClear style={{ marginBottom: 8 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.taille.mini, marginBottom: 6 }}>
        <span style={{ color: tente && manquePersonne ? T.rouge : T.muet }}>
          {manquePersonne
            ? (type === 'DIRECT' && !modeAjout ? t('modale.choisirPersonne') : t('modale.choisirAuMoinsUne'))
            : t('modale.personnesChoisies', { count: choisis.length })}
        </span>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ }}>
        {chargement && <div style={{ padding: 16, color: T.muet, fontSize: T.taille.meta }}>{t('modale.chargement')}</div>}
        {!chargement && visibles.length === 0 && <div style={{ padding: 16, color: T.muet, fontSize: T.taille.meta }}>{t('modale.personneNeCorrespond')}</div>}
        {visibles.map((c) => {
          const actif = choisis.includes(c.utilisateurTrackingId);
          return (
            <button key={c.utilisateurTrackingId} type="button" onClick={() => basculer(c.utilisateurTrackingId)} aria-pressed={actif}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '8px 12px', minHeight: 44, border: 'none',
                borderBottom: `1px solid ${T.bord}`, background: actif ? T.vertXpale : T.blanc, cursor: 'pointer', textAlign: 'left', font: 'inherit',
              }}>
              <Avatar nom={c.nomComplet} initiales={c.initiales} taille={32} presence enLigne={c.enLigne} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: T.taille.meta, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nomComplet}</span>
                <span style={{ display: 'block', fontSize: T.taille.mini, color: T.muet }}>{[c.profil, c.organisme].filter(Boolean).join(' · ')}</span>
              </span>
              {actif && <CheckOutlined style={{ color: T.vert }} />}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
